import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MattersCombobox from "../Postings/MattersComboBox";
import * as api from "../../api/index";
import { getMattersPostings } from "services/postingsService";
import { Button } from "@progress/kendo-react-buttons";
import {
  clearMattersBalances,
  getMattersBalances,
  getMattersInfo,
} from "actions/matters.action";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import ViewPostingDetails from "../Postings/PostingDetails";
import MatterAccount from "./MatterAccount";
import { clearLedgerLink } from "actions/ledgerLinks.action";
import MatterLedgerGrid from "components/LedgerGrid";

import styles from "./MatterLedger.module.scss";
import { Dialog } from "@progress/kendo-react-dialogs";
import ReportViewer from "pages/Reports/ReportViewerComponent";
import { ReportURL } from "api/axios.config";
import { BiCollapse, BiExpand } from "react-icons/bi";
import { RxCross2 } from "react-icons/rx";
import { IconBackground } from "components/common/DocumentViewer/DocumentViewerComponents";
import { FaExternalLinkAlt } from "react-icons/fa";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import LedgerPostingGrid from "components/LedgerGrid/LedgerTypeGrid";
import PageTabItemTitle from "components/tabs/TabItem/PageTabItemTitle";
import UndertakingsList from "components/Undertakings/UndertakingsList";
import {
  updateCurrentUserSettings,
  useCurrentUserSettings,
} from "services/userSettingsService";

const USER_SETTING_SHOW_DDA_KEY = "MatterLedgerShowDDAAccount";
const USER_SETTING_SHOW_REVERSALS_KEY = "MatterLedgerShowReversals";

const MatterLedger = ({ component, initialMatterId }) => {
  const dispatch = useDispatch();

  const [transNo, settransNo] = useState("");
  const [showForm, setshowForm] = useState(false);
  const [matterId, setMatterId] = useState(null);
  const [openReport, setOpenReport] = useState(false);
  const [showDDA, setShowDDA] = useState(true);
  const [showReversals, setShowReversals] = useState(false);
  const [list, setList] = useState([]);
  const [fullScreen, setfullScreen] = useState(false);
  const [fixedScroll, setFixedScroll] = useState(true);
  const [ledgerType, setLedgerType] = useState("ALL");
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [gridKey, setGridKey] = useState(0);

  const mattersInfo = useSelector((state) => state.mattersList.mattersDetails);
  const { data: userSettings } = useCurrentUserSettings();

  const redirectedMatterLedger = useSelector(
    (state) => state.ledgerLinks.currentMatterLedger
  );

  const comboboxValue = useMemo(() => {
    if (!matterId || !mattersInfo?.data?.matterId) return null;
    return {
      ...mattersInfo.data,
      matterId: mattersInfo.data.matterId,
      matterRef: mattersInfo.data.matterRef,
    };
  }, [mattersInfo, matterId]);

  const tabConfig = [
    { label: "All", type: "ALL" },
    { label: "Bills", type: "Sales" },
    { label: "Disbursements", type: "Purchase" },
    { label: "Client", type: "Client" },
    { label: "Undertakings", type: "Undertakings" },
  ];
  useEffect(() => {
    if (!matterId) return;
    dispatch(getMattersInfo(matterId));
    dispatch(getMattersBalances(matterId, "Matter Ledger"));
    if (ledgerType !== "Undertakings") {
      fetchPostings();
    }
  }, [matterId, showReversals]);

  const fetchPostings = async () => {
    if (ledgerType === "Undertakings") return;
    setLoading(true);
    try {
      const data = await getMattersPostings(matterId, ledgerType, showReversals);
      setList(data);
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userSettings?.length) return;
    const savedShowDDA = userSettings?.find(
      (s) => s.key === USER_SETTING_SHOW_DDA_KEY,
    )?.value;
    const savedShowReversals = userSettings?.find(
      (s) => s.key === USER_SETTING_SHOW_REVERSALS_KEY,
    )?.value;

    if (savedShowDDA !== undefined) {
      setShowDDA(savedShowDDA === true || savedShowDDA === "true");
    }
    if (savedShowReversals !== undefined) {
      setShowReversals(
        savedShowReversals === true || savedShowReversals === "true",
      );
    }
  }, [userSettings]);

  const handleToggleDDA = async () => {
    const nextValue = !showDDA;
    setShowDDA(nextValue);
    try {
      await updateCurrentUserSettings([
        { key: USER_SETTING_SHOW_DDA_KEY, value: nextValue },
      ]);
    } catch (error) {}
  };

  const handleToggleShowReversals = async () => {
    const nextValue = !showReversals;
    setShowReversals(nextValue);
    try {
      await updateCurrentUserSettings([
        { key: USER_SETTING_SHOW_REVERSALS_KEY, value: nextValue },
      ]);
    } catch (error) {}
  };

  useEffect(() => {
    if (component === "Standalone" && initialMatterId) {
      setMatterId(initialMatterId);
    }
  }, [component, initialMatterId]);

  useEffect(() => {
    if (redirectedMatterLedger?.matterId) {
      setMatterId(redirectedMatterLedger.matterId);
      dispatch(clearLedgerLink("MatterLedger", redirectedMatterLedger));
    }
  }, [redirectedMatterLedger]);

  useEffect(() => {
    if (component === "Matter") {
      const timer = setTimeout(() => {
        // Force re-render so initial layout is properly adjusted in the Dialog (MatterList > Matter Ledger)
        setGridKey((prev) => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [component]);

  const onChangematter = (event) => {
    const matterValue = event?.target?.value;
    if (!matterValue?.matterId) {
      clearData();
      return;
    }
    if (matterValue.matterId === matterId) return;
    setFixedScroll(false);
    setMatterId(matterValue.matterId);
  };

  const clearData = () => {
    setList([]);
    setMatterId(null);
    dispatch(clearLedgerLink("Matters", ""));
    dispatch(clearMattersBalances("Matter Ledger"));
  };

  const refreshData = () => {
    if (ledgerType === "Undertakings") {
      setRefreshTrigger((prev) => !prev);
    } else {
      fetchPostings();
      dispatch(getMattersInfo(matterId));
      dispatch(getMattersBalances(matterId, "Matter Ledger"));
    }
    setFixedScroll(false);
  };

  const renderTabContent = (type) => {
    if (type === "ALL") {
      return renderMatterLedgerGrid();
    }

    if (type === "Undertakings") {
      return (
        <UndertakingsList
          matterId={matterId}
          clientId={mattersInfo?.data?.clientId}
          refreshData={refreshTrigger}
          showInTabs={true}
        />
      );
    }

    return (
      <LedgerPostingGrid
        loading={loading}
        gridData={list}
        ledgerType={type}
        matterId={matterId}
        matterRef={mattersInfo?.data?.matterRef}
        handleActionRef={(e, transNo) => {
          settransNo(transNo);
          setshowForm(true);
        }}
      />
    );
  };

  const DialogTitleBar = (titleText) => {
    return (
      <div className="w-100 d-flex align-items-center justify-content-between">
        <div>{titleText}</div>
        <div className="d-flex align-items-center">
          <IconBackground onClick={() => setfullScreen(!fullScreen)}>
            {fullScreen ? (
              <BiCollapse size={20} role="button" />
            ) : (
              <BiExpand size={20} role="button" />
            )}
          </IconBackground>
          <hr className={`${styles["vertical-border"]} ms-3 me-3`} />
          <IconBackground onClick={() => setOpenReport(false)} className="me-2">
            <RxCross2 size={20} color="red" role="button" />
          </IconBackground>
        </div>
      </div>
    );
  };

  const handleOpenStandalone = () => {
    if (matterId) {
      window.open(`/sa/matterledger/${matterId}`, "_blank");
    }
  };

  const renderMatterLedgerGrid = () => (
    <MatterLedgerGrid
      gridKey={gridKey}
      loading={loading}
      gridData={list}
      showDDA={showDDA}
      setShowDDA={handleToggleDDA}
      showReversals={showReversals}
      setShowReversals={handleToggleShowReversals}
      fixedScroll={fixedScroll}
      setOpenReport={setOpenReport}
      matterId={matterId}
      isClosed={mattersInfo?.data?.matterId && mattersInfo?.data?.closed}
      handleActionRef={(e, transNo) => {
        settransNo(transNo);
        setshowForm(true);
      }}
      prevTransTitle={`Matter Previous Transactions [${mattersInfo?.data?.matterRef} - ${mattersInfo?.data?.details}]`}
      matterRef={mattersInfo?.data?.matterRef}
    />
  );

  return (
    <div className="page h-100 w-100 d-flex flex-column">
      <div className="row w-100">
        <div className="col mt-1">
          {component !== "Matter" && (
            <div className="mb-3">
              <label className="me-3">Matter Ref</label>
              <MattersCombobox
                controlled
                value={comboboxValue}
                onChangematter={onChangematter}
                onFilterChange={clearData}
                width={280}
              />
              <Button
                className="common-btn ms-2"
                svgIcon={refreshIcon}
                onClick={refreshData}
                disabled={!matterId || loading}>
                Refresh
              </Button>
            </div>
          )}

          <div className="row fs-md">
            <div className="col-1" style={{ minWidth: "80px" }}>
              Details:{" "}
            </div>
            <div className="col">{matterId ? mattersInfo?.data?.details : ""}</div>
          </div>
          <div className="row fs-md">
            <div className="col-1" style={{ minWidth: "80px" }}>
              Branch:{" "}
            </div>
            <div className="col">{matterId ? mattersInfo?.data?.branch : ""}</div>
          </div>
          <div className="row fs-md">
            <div className="col-1" style={{ minWidth: "80px" }}>
              App:{" "}
            </div>
            <div className="col">
              {matterId ? mattersInfo?.data?.application : ""}
            </div>
          </div>
        </div>

        <div
          className={`${
            component === "Matter" ? "col-5" : "col-xxl-4 "
          } d-flex justify-content-end mt-2 mt-xxl-0`}>
          <MatterAccount component={"Matter Ledger"} matterId={matterId} />
        </div>

        {showForm && (
          <ViewPostingDetails
            reloadData={refreshData}
            transNo={transNo}
            cancelEdit={() => {
              setshowForm(false);
            }}
          />
        )}
      </div>

      {component !== "Matter" ? (
        <div className="mt-3 flex-grow-1 position-relative overflow-auto">
          <TabStrip
            selected={selectedTab}
            className={`pagetab h-100`}
            onSelect={(e) => {
              const selectedIndex = e.selected;
              setSelectedTab(selectedIndex);
              setLedgerType(tabConfig[selectedIndex].type);
            }}>
            {tabConfig.map((tab, index) => (
              <TabStripTab
                key={tab.label}
                contentClassName="h-100"
                title={
                  <PageTabItemTitle
                    title={tab.label}
                    index={index}
                    selected={selectedTab}
                  />
                }>
                <div className="mt-2 h-100 pb-2">{renderTabContent(tab.type)}</div>
              </TabStripTab>
            ))}
          </TabStrip>
          {matterId && component !== "Standalone" && (
            <div className={styles.standaloneIconWrapper}>
              <button
                className={`btn p-0 border-0 ${styles.standaloneIconButton}`}
                onClick={handleOpenStandalone}
                title="Open in New Tab">
                <div className={styles.standaloneIconContainer}>
                  <FaExternalLinkAlt size={18} />
                </div>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className="mt-2 flex-grow-1 d-flex flex-column overflow-hidden"
          style={{ minHeight: 0 }}>
          {renderMatterLedgerGrid()}
        </div>
      )}

      {openReport && (
        <Dialog
          title={DialogTitleBar("Matter Ledger Report")}
          onClose={() => setOpenReport(false)}
          className={` dialog pdfwindow fs-md  ${
            fullScreen && " dialog-fullscreen "
          }`}
          minWidth={"60vw"}>
          <div
            className="px-3 pt-1"
            style={{ height: fullScreen && "calc( 100vh - 120px )" }}>
            <ReportViewer
              modalHeight={!fullScreen && "65vh"}
              name="MatterLedger.trdp"
              parameters={{
                matterId: matterId,
                reportDocumentName: `Matter Ledger - ${mattersInfo?.data?.matterRef}`,
              }}
              url={`${ReportURL}/Reporting/reports`}
            />
          </div>
          <div className="dialog-footer">
            <button
              className="common-btn me-5"
              type={"submit"}
              onClick={() => setOpenReport(false)}>
              Close
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default MatterLedger;
